import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Typography,
} from '@mui/material';
import { useAtom, useSetAtom } from 'jotai';
import { selectedFiberSitePlanningRow, allSitesFiberSitePlanning } from '../../common/atom/atoms';
import { updateFiberSite } from './functions/update_fiber_site.function';
import { notify } from '../../common/functions/react-toastify.function';
import { useBackdrop } from '../../common/components/backDrop_context.component';
import { useFiberDashboard } from './context/fiberDashboardContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import FiberSiteHistoryPanel from './FiberSiteHistoryPanel';

const fieldLabels = {
  site_code: 'Site ID',
  project_name: 'Project Name',
  phase_name: 'Phase Name',
  bp: 'BP',
  fiber_site_name: 'Site Name',
  lat: 'Latitude',
  long: 'Longitude',
  city: 'City',
  region: 'Region',
  vendor: 'Vendor',
  fiber_pop_id: 'Fiber POP ID',
  ring_name: 'Ring Name',
  ring_unique_name: 'Ring Unique Name',
  ring_name_nms: 'Ring Name on NMS',
  status: 'Status',
  cutover_date: 'Cutover Date',
  total_length: "Total Length (Mtr's)",
  on_net: "On net (Mtr's)",
  off_net: "Offnet (Mtr's)",
  prefix: 'Prefix',
  old_site_code: 'Old Site Code',
  onfp_planning_remarks: 'OFNP Planning Remarks',
  bp_remarks: 'BP Remarks',
  comments: 'Comments',
  replacement_site_code: 'Replacement Site Code',
  replacement_site_name: 'Replacement Site Name',
  replacement_sit_lat: 'Replacement Site Lat',
  replacement_sit_long: 'Replacement Site Long',
  replacement_site_proposed_ring: 'Replacement Site Proposed Ring',
  addendum_status: 'Addendum Status',
  descope_status: 'Descope Status',
  is_ftts: 'Is FTTS',
};

const statusOptions = ['In Progress', 'On Air', 'Planned', 'Non Fiber', 'Descoped'];

const displayFields = [
  'site_code',
  'region',
  'vendor',
  'coverage_type',
  'site_status',
  'usf_site',
  'physical',
  'status',
  'onfp_planning_remarks',
  'bp_remarks',
  'comments',
];

const options = {
  physical: ['2G', '3G', '4G', '2G+4G'],
  vendor: ['Huawei', 'Ericsson', 'ZTE'],
  region: ['North', 'South', 'Central A', 'Central B'],
  coverage_type: ['Urban', 'Rural', 'Macro'],
  part_of_dec_cfo: ['Yes', 'No'],
  site_status: ['Active', 'Inactive'],
  usf_site: ['Yes', 'No'],
  commercial_district: ['Rahim Yar Khan', 'District A', 'District B'],
  commercial_city: ['Liaqatpur_Rural', 'City A', 'City B'],
  mbu: ['C7-KNP-06', 'MBU 1', 'MBU 2'],
  commercial_subregion: ['Central 7', 'Subregion 1', 'Subregion 2'],
  commercial_region: ['Central B', 'Region 1', 'Region 2'],
  pta_tehsil: ['LIAQUATPUR TEHSIL', 'Tehsil A', 'Tehsil B'],
  pta_district: ['RAHIM YAR KHAN', 'District X', 'District Y'],
  pta_province: ['PUNJAB', 'Province A', 'Province B'],
  prefix: ['ABB', 'Prefix A', 'Prefix B'],
  city_prefix_based: ['Abbasia', 'City A', 'City B'],
  technical_city: ['Rural', 'Urban', 'Tech City A'],
  site_type: ['Tower co B2S', 'Type A', 'Type B'],
  sharing_type_hostguest: ['Host', 'Guest'],
  omo_name: ['Enfrashare', 'OMO A', 'OMO B'],
  b2s_id: ['ES2-LQP-01977', 'B2S-001', 'B2S-002'],
  bsc: ['RYK317Z', 'BSC A', 'BSC B'],
  bsc_code: ['RYK317Z_MGW_2', 'CODE-001', 'CODE-002'],
  '4g_capacity': ['2T2R', 'Capacity A', 'Capacity B'],
  status: ['In-Progress', 'On-Air', 'Planned', 'Non-Fiber', 'Descoped'],
  is_ftts: ['Yes', 'No'],
};

const editableFields = [
  'status',
  'onfp_planning_remarks',
  'bp_remarks',
  'comments',
  'project_name',
  'phase_name',
  'bp',
  'fiber_site_name',
  'lat',
  'long',
  'technical_city',
  'region',
  'vendor',
  'fiber_pop_id',
  'ring_name',
  'ring_unique_name',
  'ring_name_nms',
  'cutover_date',
  'total_length',
  'on_net',
  'off_net',
  'prefix',
  'old_site_code',
  'replacement_site_code',
  'replacement_site_name',
  'replacement_site_lat',
  'replacement_site_long',
  'replacement_site_proposed_ring',
  'addendum_status',
  'descope_status',
];

const allFields = [
  'site_code',
  'project_name',
  'status',
  'onfp_planning_remarks',
  'bp_remarks',
  'comments',
  'phase_name',
  'bp',
  'fiber_site_name',
  'lat',
  'long',
  'technical_city',
  'region',
  'vendor',
  'fiber_pop_id',
  'ring_name',
  'ring_unique_name',
  'ring_name_nms',
  'cutover_date',
  'total_length',
  'on_net',
  'off_net',
  'prefix',
  'old_site_code',
  'replacement_site_code',
  'replacement_site_name',
  'replacement_site_lat',
  'replacement_site_long',
  'replacement_site_proposed_ring',
  'addendum_status',
  'descope_status',
];

export const EditSingleSiteFiberSitePlanning = () => {
  const [selectedRow, setSelectedRow] = useAtom(selectedFiberSitePlanningRow);
  const [allSites, setAllSites] = useAtom(allSitesFiberSitePlanning);
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const { refreshDashboardData } = useFiberDashboard();
  const [formData, setFormData] = useState({});
  const [auditLogData, setAuditLogData] = useState(null);

  useEffect(() => {
    if (selectedRow) {
      setFormData({
        ...selectedRow,
        // Ensure off_net is always initialized, even if missing from selectedRow
        off_net: selectedRow.off_net !== undefined ? selectedRow.off_net : '',
      });
    }
  }, [selectedRow]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    showBackdrop();
    try {
      // Ensure off_net is always included in the payload, even if empty
      const payloadData = { ...formData };
      if (!payloadData.hasOwnProperty('off_net')) {
        payloadData.off_net = selectedRow?.off_net || '';
      }
      await updateFiberSite(selectedRow.id, payloadData);
      setAllSites((prevSites) =>
        prevSites.map((site) => (site.id === selectedRow.id ? { ...site, ...formData } : site))
      );

      // Refresh dashboard data to get updated information
      try {
        await refreshDashboardData();
        console.log('Dashboard data refreshed after editing single site');

        // Also refresh the detailed table data to update count
        window.dispatchEvent(new CustomEvent('refreshFiberTableData'));
      } catch (error) {
        console.error('Error refreshing dashboard data:', error);
        // Don't show error to user as the main operation was successful
      }

      notify('success', 'Fiber site updated successfully');
      setSelectedRow(null);
    } catch (error) {
      console.error('Error updating fiber site:', error);
      notify('error', 'Failed to update fiber site. Please try again.');
    } finally {
      hideBackdrop();
    }
  };

  const handleAuditLogClick = (auditLog, siteIndex = 0) => {
    console.log('Fiber site audit log clicked:', auditLog, 'Site index:', siteIndex);
    if (
      auditLog.details &&
      auditLog.details.updatedSites &&
      auditLog.details.updatedSites[siteIndex]
    ) {
      const auditSiteData = auditLog.details.updatedSites[siteIndex];

      // Map audit log data to form state for fiber site fields
      const mappedAuditData = {
        site_code: auditSiteData.site_code || '',
        project_name: auditSiteData.project_name || '',
        status: auditSiteData.status || '',
        onfp_planning_remarks: auditSiteData.onfp_planning_remarks || '',
        bp_remarks: auditSiteData.bp_remarks || '',
        comments: auditSiteData.comments || '',
        phase_name: auditSiteData.phase_name || '',
        bp: auditSiteData.bp || '',
        fiber_site_name: auditSiteData.fiber_site_name || '',
        lat: auditSiteData.lat || null,
        long: auditSiteData.long || null,
        technical_city: auditSiteData.technical_city || '',
        region: auditSiteData.region || '',
        vendor: auditSiteData.vendor || '',
        fiber_pop_id: auditSiteData.fiber_pop_id || '',
        ring_name: auditSiteData.ring_name || '',
        ring_unique_name: auditSiteData.ring_unique_name || '',
        ring_name_nms: auditSiteData.ring_name_nms || '',
        cutover_date: auditSiteData.cutover_date || '',
        total_length: auditSiteData.total_length || '',
        on_net: auditSiteData.on_net || '',
        off_net: auditSiteData.off_net || '',
        prefix: auditSiteData.prefix || '',
        old_site_code: auditSiteData.old_site_code || '',
        replacement_site_code: auditSiteData.replacement_site_code || '',
        replacement_site_name: auditSiteData.replacement_site_name || '',
        replacement_site_lat: auditSiteData.replacement_site_lat || '',
        replacement_site_long: auditSiteData.replacement_site_long || '',
        replacement_site_proposed_ring: auditSiteData.replacement_site_proposed_ring || '',
        addendum_status: auditSiteData.addendum_status || '',
        descope_status: auditSiteData.descope_status || '',
      };

      // Store original data if not already stored
      if (!auditLogData) {
        setAuditLogData(formData);
      }

      // Update form with audit log data
      setFormData(mappedAuditData);
    }
  };

  const clearAuditState = () => {
    if (auditLogData) {
      setFormData(auditLogData);
    }
    setAuditLogData(null);
  };

  // Function to check if a field has changed (for highlighting)
  const isFieldChanged = (fieldName) => {
    if (!auditLogData || !selectedRow) {
      return false;
    }

    const currentValue = selectedRow[fieldName];
    const historicalValue = formData[fieldName];

    // Convert to strings for comparison, handling null/undefined
    const currentStr = currentValue?.toString() || '';
    const historicalStr = historicalValue?.toString() || '';

    return currentStr !== historicalStr;
  };

  if (!selectedRow) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, display: 'flex', gap: 2, height: 'calc(100vh)' }}>
      {/* Main Edit Form - 70% */}
      <Box
        flex={7}
        sx={{ background: 'white', p: 3, borderRadius: 2, boxShadow: 2, overflowY: 'auto' }}
      >
        <Box display="flex" alignItems="center" mb={2}>
          <IconButton onClick={() => setSelectedRow(null)} size="large" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <span style={{ fontWeight: 600, fontSize: 22, color: '#1C274C' }}>Update Site</span>
        </Box>
        <Grid container spacing={2}>
          {allFields
            .filter((key) => formData.hasOwnProperty(key)) // Only show fields that exist in the data
            .map((key) => (
              <Grid item xs={4} key={key}>
                {editableFields.includes(key) ? (
                  // Render editable fields with light highlight
                  key === 'status' ? (
                    <FormControl fullWidth size="small">
                      <InputLabel>{fieldLabels[key] || key}</InputLabel>
                      <Select
                        value={formData[key] || ''}
                        label={fieldLabels[key] || key}
                        onChange={(e) => handleChange(key, e.target.value)}
                        sx={{
                          borderRadius: '6px',
                          backgroundColor: isFieldChanged(key) ? '#fff3e0' : '#f8f9ff', // Light orange for changed fields
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#BD1D23',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#BD1D23',
                          },
                        }}
                      >
                        {options[key]?.map((option) => (
                          <MenuItem value={option} key={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      label={fieldLabels[key] || key}
                      value={formData[key] || ''}
                      onChange={(e) => handleChange(key, e.target.value)}
                      multiline
                      // minRows={2}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                          backgroundColor: isFieldChanged(key) ? '#fff3e0' : '#f8f9ff', // Light orange for changed fields
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#BD1D23',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#BD1D23',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#BD1D23',
                        },
                      }}
                    />
                  )
                ) : (
                  // Render read-only fields
                  <TextField
                    fullWidth
                    label={fieldLabels[key] || key}
                    value={formData[key] || ''}
                    disabled
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: isFieldChanged(key) ? '#fff3e0' : 'transparent', // Light orange for changed fields
                      },
                    }}
                  />
                )}
              </Grid>
            ))}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              {auditLogData && (
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#ff6b35',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      mr: 2,
                    }}
                  >
                    Viewing historical data
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={clearAuditState}
                    sx={{
                      borderColor: '#ff6b35',
                      color: '#ff6b35',
                      '&:hover': {
                        borderColor: '#e55a2b',
                        backgroundColor: '#fff5f2',
                      },
                    }}
                  >
                    Restore Latest
                  </Button>
                </Box>
              )}
              <Box display="flex" gap={2}>
                <Button variant="outlined" onClick={() => setSelectedRow(null)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  style={{ backgroundColor: '#BD1D23' }}
                  onClick={handleSave}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* History Panel - 30% */}
      <Box flex={3} minWidth={250} maxWidth={400}>
        <FiberSiteHistoryPanel
          siteCode={formData.site_code}
          onAuditLogClick={handleAuditLogClick}
          onClose={clearAuditState}
        />
      </Box>
    </Box>
  );
};
